import React, { useState } from 'react';
import { Modal } from '../common/Modal/Modal';
import styles from './VragenModule.module.css';

interface AddQuestionModalProps {
  onClose: () => void;
  onSave: (question: any) => void;
}

export const AddQuestionModal: React.FC<AddQuestionModalProps> = ({ onClose, onSave }) => {
  const [formData, setFormData] = useState({
    question: '',
    source: '',
    author: '',
    answer: '',
    status: 'open',
    tags: '',
    notes: ''
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.question.trim()) {
      newErrors.question = 'Vraag is verplicht';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (validate()) {
      const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      onSave({
        ...formData,
        tags: tagsArray,
        createdAt: new Date().toISOString()
      });
      onClose();
    }
  };

  return (
    <Modal isOpen={true} onClose={onClose} title="Nieuwe vraag toevoegen">
      <form onSubmit={handleSubmit} className={styles.modalForm}>
        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
              <line x1="12" y1="17" x2="12.01" y2="17" />
            </svg>
            Vraag details
          </h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Vraag
              <span className={styles.required}>*</span>
            </label>
            <textarea
              name="question"
              value={formData.question}
              onChange={handleChange}
              className={`${styles.textarea} ${errors.question ? styles.error : ''}`}
              rows={3}
              placeholder="Stel hier de vraag..."
            />
            {errors.question && <span className={styles.errorText}>{errors.question}</span>}
          </div>

          <div className={styles.formRow}>
            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="8.5" cy="7" r="4" />
                  <polyline points="17 11 19 13 23 9" />
                </svg>
                Auteur
                <span className={styles.helpText}>Optioneel</span>
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                className={styles.input}
                placeholder="Naam of email van de vraagsteller"
              />
            </div>

            <div className={styles.formGroup}>
              <label className={styles.label}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                  <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                </svg>
                Bron
                <span className={styles.helpText}>Optioneel</span>
              </label>
              <select
                name="source"
                value={formData.source}
                onChange={handleChange}
                className={styles.select}
              >
                <option value="">Selecteer een bron...</option>
                <option value="Email">Email</option>
                <option value="Telefoon">Telefoon</option>
                <option value="Website">Website</option>
                <option value="Social Media">Social Media</option>
                <option value="Bijeenkomst">Bijeenkomst</option>
                <option value="Klachtenformulier">Klachtenformulier</option>
                <option value="Informeel gesprek">Informeel gesprek</option>
                <option value="Brief">Brief</option>
              </select>
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
                <line x1="7" y1="7" x2="7.01" y2="7" />
              </svg>
              Tags
              <span className={styles.helpText}>Gescheiden door komma's</span>
            </label>
            <input
              type="text"
              name="tags"
              value={formData.tags}
              onChange={handleChange}
              className={styles.input}
              placeholder="bijv. urgent, bewoners, fase 2"
            />
          </div>
        </div>

        <div className={styles.formSection}>
          <h3 className={styles.sectionTitle}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 20h9" />
              <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
            </svg>
            Antwoord & Status
          </h3>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              Antwoord
              <span className={styles.helpText}>Optioneel</span>
            </label>
            <textarea
              name="answer"
              value={formData.answer}
              onChange={handleChange}
              className={styles.textarea}
              rows={4}
              placeholder="Geef hier het antwoord op de vraag..."
            />
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
              </svg>
              Status
            </label>
            <select
              name="status"
              value={formData.status}
              onChange={handleChange}
              className={styles.select}
            >
              <option value="open">Open</option>
              <option value="in_behandeling">In behandeling</option>
              <option value="beantwoord">Beantwoord</option>
              <option value="gesloten">Gesloten</option>
            </select>
          </div>
        </div>

        <div className={styles.formSection}>
          <div className={styles.formGroup}>
            <label className={styles.label}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ opacity: 0.7 }}>
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
                <polyline points="10 9 9 9 8 9" />
              </svg>
              Interne notities
            </label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              className={styles.textarea}
              rows={3}
              placeholder="Interne opmerkingen of notities..."
            />
          </div>
        </div>

        <div className={styles.modalActions}>
          <button type="button" onClick={onClose} className={styles.btnSecondary}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            Annuleren
          </button>
          <button type="submit" className={styles.btnPrimary}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>
            Vraag toevoegen
          </button>
        </div>
      </form>
    </Modal>
  );
};